import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import {
  designColors,
  designShell,
  designTheme,
  designType,
  HEADER_ACTION_BUTTON_WIDTH,
} from "services/constants/designTokens";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { IconSectionCard } from "components/DesignSystem/IconSectionCard";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { showToast } from "services/utils/Status";
import { customAPI, customGetAPIQuiet } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import { isOrganisationResponse, organisationFromApi, organisationToApi, organisationUrl } from "./organisationApi";
import { validateOrganisation } from "./organisationRules";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import { notifySetupProgress } from "components/SetupFlow/progress";

const requiredMark = (
  <Mui.Box component="span" sx={{ color: "#DC2626", ml: 0.5 }}>
    *
  </Mui.Box>
);

const emptyForm = { organisationId: "", organisationName: "" };

// Locked fields are read-only rather than disabled: MUI fades disabled
// text, and these values still need to read clearly. The grey fill and
// unchanged border on focus mark them as not editable.
const lockedFieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root": {
    ...globalTextFieldSx["& .MuiOutlinedInput-root"],
    backgroundColor: "#F8FAFC",
    "&:hover fieldset, &.Mui-focused fieldset": {
      borderColor: designColors.borderInput,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": { color: designColors.slate900, cursor: "default" },
};

const ADMINISTRATION_VIEW = {
  editable: false,
  trail: [
    { label: "Administration", path: "/admin/administration/user" },
    { label: "Organisation" },
    { label: "Organisation Details" },
  ],
  subtitle: "View the organisation identity information.",
};

const SYSTEM_CONFIGURATION_VIEW = {
  editable: true,
  trail: [
    { label: "System Configuration", path: "/admin/support/admin" },
    { label: "Organisation Details" },
  ],
  subtitle: "Manage the organisation identity information.",
};

// The organisation identity. It is maintained under System Configuration >
// Organisation; Administration > Organisation > Organisation Details shows
// the same record read-only. Contacts have their own page
// (pages/Administration/Organisation/OrganisationContacts).
function OrganisationDetails({ permissions, view }) {
  // Without the edit permission (or on the read-only Administration copy)
  // the details are shown locked, with no Save or Discard. This screen has
  // no delete action.
  const canEdit = view.editable && permissions?.canEdit !== false;
  const history = useHistory();
  const { domain } = useSelector(state => state.tokenReducer);
  const [saved, setSaved] = useState(emptyForm);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [resetOpen, setResetOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);
  const requestRef = useRef(null);

  useEffect(() => {
    const request = { active: true, saving: false };
    requestRef.current = request;
    setLoading(true);
    setSaving(false);
    setErrors({});
    setResetOpen(false);
    setForm(emptyForm);
    setSaved(emptyForm);
    // A new tenant has no organisation record yet, so the GET fails or comes
    // back empty. That is not an error to show: the form opens empty and the
    // first save creates the record (POST).
    customGetAPIQuiet(organisationUrl(domain)).then(result => {
      if (!request.active) return;
      if (result?.ok && isOrganisationResponse(result.responseDetails)) {
        const values = organisationFromApi(result.responseDetails);
        setForm(values);
        setSaved(values);
        setIsDefault(result.responseDetails.is_default === true);
      } else setIsDefault(true);
      setLoading(false);
    });
    return () => { request.active = false; };
  }, [domain]);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(saved),
    [form, saved],
  );

  const updateField = (key, value) => {
    if (requestRef.current?.saving) return;
    setForm((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: "" }));
  };

  const handleSave = async () => {
    const request = requestRef.current;
    if (!canEdit || !request?.active || request.saving || loading) return;
    const nextErrors = validateOrganisation(form);
    setErrors(nextErrors);
    // Each field shows its own error underneath, so no toast as well.
    if (Object.keys(nextErrors).length) return;
    request.saving = true;
    setSaving(true);
    try {
      const payload = organisationToApi(form);
      const result = await customAPI(organisationUrl(domain), isDefault ? "POST" : "PUT", payload);
      if (!request.active || !result?.ok) return;
      const values = organisationFromApi(isOrganisationResponse(result.responseDetails) ? result.responseDetails : payload);
      setSaved(values);
      setForm(values);
      setIsDefault(false);
      showToast("success", result.responseDetails?.message || "Organisation details saved.");
      notifySetupProgress("details");
    } catch (error) {
      if (request.active) showToast("error", error.message || "Organisation details could not be saved.");
    } finally {
      request.saving = false;
      if (request.active) setSaving(false);
    }
  };

  const handleReset = () => {
    if (requestRef.current?.saving) return;
    setForm(saved);
    setErrors({});
    setResetOpen(false);
  };

  if (loading) return <Loader info="Loading..." />;

  return (
    <Mui.ThemeProvider theme={designTheme}>
      <Mui.Box sx={{ ...designShell.pageSurface, p: { xs: 2, md: 3 } }}>
        <Mui.Stack spacing={2}>
          <GlobalBreadcrumb
            trail={view.trail.map(({ label, path }) => ({
              label,
              ...(path && { onClick: () => history.push(path) }),
            }))}
          />
          <Mui.Box>
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              Organisation Details
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              {view.subtitle}
            </Mui.Typography>
          </Mui.Box>

          <IconSectionCard
            icon={<ApartmentOutlined />}
            title="Organisation Details"
            subtitle="Basic identity information for this organisation."
          >
            {/* One field per row, each spanning the full card width. */}
            <Mui.Stack spacing={2.5}>
              <Mui.Box>
                <GlobalFieldLabel>
                  Organisation ID
                  {requiredMark}
                </GlobalFieldLabel>
                <Mui.TextField
                  fullWidth
                  size="small"
                  value={form.organisationId}
                  disabled={saving}
                  onChange={(event) => updateField("organisationId", event.target.value)}
                  error={Boolean(errors.organisationId)}
                  helperText={
                    errors.organisationId ||
                    "Unique identifier for this organisation. This ID is shared across all tenants belonging to the organisation."
                  }
                  inputProps={{ "aria-label": "Organisation ID", readOnly: !canEdit }}
                  sx={canEdit ? globalTextFieldSx : lockedFieldSx}
                />
              </Mui.Box>
              <Mui.Box>
                <GlobalFieldLabel>
                  Organisation Name
                  {requiredMark}
                </GlobalFieldLabel>
                <Mui.TextField
                  fullWidth
                  size="small"
                  value={form.organisationName}
                  disabled={saving}
                  onChange={(event) => updateField("organisationName", event.target.value)}
                  error={Boolean(errors.organisationName)}
                  helperText={errors.organisationName}
                  inputProps={{ "aria-label": "Organisation Name", readOnly: !canEdit }}
                  sx={canEdit ? globalTextFieldSx : lockedFieldSx}
                />
              </Mui.Box>
            </Mui.Stack>
          </IconSectionCard>

          {/* Page actions sit at the foot of the form, right-aligned, the
              same placement as Organisation > Settings. */}
          {canEdit && <Mui.Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            justifyContent="flex-end"
            spacing={1.5}
          >
            <SecondaryButton
              startIcon={<RestartAltRounded />}
              onClick={() => setResetOpen(true)}
              disabled={!isDirty || saving}
              sx={{ minWidth: HEADER_ACTION_BUTTON_WIDTH }}
            >
              Discard Changes
            </SecondaryButton>
            <PrimaryButton
              startIcon={<SaveOutlined />}
              onClick={handleSave}
              disabled={!isDirty || saving}
              loading={saving}
              sx={{ minWidth: HEADER_ACTION_BUTTON_WIDTH }}
            >
              Save Changes
            </PrimaryButton>
          </Mui.Stack>}
        </Mui.Stack>
      </Mui.Box>

      <GlobalConfirmDialog
        open={resetOpen}
        title="Discard changes"
        onCancel={() => setResetOpen(false)}
        onConfirm={handleReset}
        confirmLabel="Discard"
        cancelLabel="Cancel"
        danger
      >
        This discards your unsaved changes and restores the last loaded or saved
        organisation details.
      </GlobalConfirmDialog>
    </Mui.ThemeProvider>
  );
}

const AdministrationOrganisationDetails = (props) => (
  <OrganisationDetails {...props} view={ADMINISTRATION_VIEW} />
);

const SystemConfigurationOrganisationDetails = (props) => (
  <OrganisationDetails {...props} view={SYSTEM_CONFIGURATION_VIEW} />
);

export const SystemOrganisationDetailsPage = withScreenPermissions(
  SystemConfigurationOrganisationDetails,
  SCREEN_ACCESS.systemOrganisation,
  "Organisation Details",
);

export default withScreenPermissions(AdministrationOrganisationDetails, SCREEN_ACCESS.organisationDetails, "Organisation Details");
